import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Badge } from '../../../components/ui/badge';
import { Globe, Save, Loader2, Info, Calendar as CalendarIcon } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

// Curated list of common IANA timezones for the dropdown.
// Falls back to allowing any string the user types (Intl.supportedValuesOf
// would be richer but compatibility varies by browser).
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Toronto',
  'America/Mexico_City',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TimezoneSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverState, setServerState] = useState(null);
  const [operatingTz, setOperatingTz] = useState('UTC');
  const [displayMode, setDisplayMode] = useState('local');

  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getTimezoneSettings();
        setServerState(data);
        setOperatingTz(data.operating_timezone);
        setDisplayMode(data.display_mode);
      } catch {
        toast.error('Failed to load timezone settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isDirty = useMemo(() => {
    if (!serverState) return false;
    return (
      operatingTz !== serverState.operating_timezone ||
      displayMode !== serverState.display_mode
    );
  }, [serverState, operatingTz, displayMode]);

  // Live preview — render "right now" both ways
  const previews = useMemo(() => {
    const now = new Date();
    const opts = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
    };
    const local = now.toLocaleString('en-US', opts);
    let operating = '—';
    try {
      operating = now.toLocaleString('en-US', { ...opts, timeZone: operatingTz });
    } catch {
      operating = 'Invalid timezone';
    }
    return { local, operating };
  }, [operatingTz]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await adminService.updateTimezoneSettings({
        operating_timezone: operatingTz,
        display_mode: displayMode,
      });
      setServerState(data);
      toast.success('Timezone settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!serverState) return;
    setOperatingTz(serverState.operating_timezone);
    setDisplayMode(serverState.display_mode);
  };

  const tzOptions = useMemo(() => {
    const set = new Set(COMMON_TIMEZONES);
    if (operatingTz) set.add(operatingTz);
    if (browserTz) set.add(browserTz);
    return Array.from(set).sort();
  }, [operatingTz, browserTz]);

  return (
    <div className="space-y-6" data-testid="timezone-settings-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Globe className="h-6 w-6 text-amber-600" />
          Timezone Settings
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Choose how dates are rendered across the client portal. Pick the bakery's
          operating timezone, then decide whether the client portal should follow each
          viewer's browser timezone or always use the operating timezone.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Operating Timezone</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="operating-tz">Bakery's Operating Timezone</Label>
                  <Select value={operatingTz} onValueChange={setOperatingTz}>
                    <SelectTrigger id="operating-tz" data-testid="operating-tz-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {tzOptions.map((tz) => (
                        <SelectItem key={tz} value={tz} data-testid={`tz-${tz.replace('/', '-')}`}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Your browser is currently set to <code className="bg-gray-100 px-1 rounded">{browserTz}</code>.
                  </p>
                </div>
              </div>

              <div>
                <Label className="block mb-2">Display Mode</Label>
                <RadioGroup
                  value={displayMode}
                  onValueChange={setDisplayMode}
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="mode-local"
                    className={`border rounded-lg p-4 cursor-pointer flex gap-3 items-start transition ${
                      displayMode === 'local' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="mode-local-card"
                  >
                    <RadioGroupItem value="local" id="mode-local" data-testid="mode-local-radio" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Browser timezone (default)</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Each client sees dates in their own local timezone — natural for most
                        wholesale flows.
                      </p>
                    </div>
                  </label>
                  <label
                    htmlFor="mode-operating"
                    className={`border rounded-lg p-4 cursor-pointer flex gap-3 items-start transition ${
                      displayMode === 'operating' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="mode-operating-card"
                  >
                    <RadioGroupItem value="operating" id="mode-operating" data-testid="mode-operating-radio" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Operating timezone</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Every viewer sees dates in the bakery's timezone. Useful when production
                        cut-offs must match the bakery's day, not the customer's day.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {/* Live preview */}
              <div
                className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2"
                data-testid="timezone-preview"
              >
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm space-y-2">
                    <p className="font-medium text-amber-900">Live preview — right now</p>
                    <p className="text-amber-900/80">
                      In <strong>your browser</strong> ({browserTz}): {previews.local}
                    </p>
                    <p className="text-amber-900/80">
                      In <strong>operating timezone</strong> ({operatingTz}): {previews.operating}
                    </p>
                    <p className="text-xs text-amber-800/70 pt-1">
                      With <strong>{displayMode === 'operating' ? 'Operating timezone' : 'Browser timezone'}</strong>{' '}
                      mode, clients will see dates rendered in{' '}
                      <strong>{displayMode === 'operating' ? `the ${operatingTz}` : 'their own browser'}</strong> timezone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Last updated */}
              {serverState?.updated_at && (
                <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="timezone-meta">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Last updated {formatDateTime(serverState.updated_at)}
                  {serverState.updated_by_email && (
                    <Badge variant="outline" className="ml-1">
                      {serverState.updated_by_email}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2 border-t">
                <Button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="save-timezone-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" /> Save Changes
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRevert}
                  disabled={!isDirty || saving}
                  data-testid="revert-timezone-btn"
                >
                  Revert
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimezoneSettings;
